import { randomUuid } from '@/practice/crypto';

import type {
  ClientMediaTelemetryDebug,
  ClientMediaTelemetryPayload,
  ClientMediaTelemetryReporter,
  KickstartLeavePhase,
  MediaProvider,
} from './types';

const MAX_DEBUG_ELAPSED_MS = 3_600_000;

type CandidateType = 'host' | 'srflx' | 'prflx' | 'relay';
type TransportProtocol = 'udp' | 'tcp' | 'tls';

export type ClientTelemetryNetworkSnapshot = {
  sampleCount: number;
  transportProtocol: TransportProtocol | null;
  localCandidateType: CandidateType | null;
  remoteCandidateType: CandidateType | null;
  relayUsed: boolean | null;
  rttMs: number | null;
  jitterMs: number | null;
  packetLossPercent: number | null;
  bytesSent: number | null;
  bytesReceived: number | null;
};

export const EMPTY_NETWORK_SNAPSHOT: ClientTelemetryNetworkSnapshot = {
  sampleCount: 0,
  transportProtocol: null,
  localCandidateType: null,
  remoteCandidateType: null,
  relayUsed: null,
  rttMs: null,
  jitterMs: null,
  packetLossPercent: null,
  bytesSent: null,
  bytesReceived: null,
};

export class ClientMediaTelemetryTracker {
  private readonly provider: MediaProvider;
  private readonly reporter: ClientMediaTelemetryReporter | undefined;
  private sessionId: string | null = null;
  private startedAtMs: number | null = null;
  private connectMs: number | null = null;
  private joinMs: number | null = null;
  private firstRemoteAudioMs: number | null = null;
  private reconnectCount = 0;
  private reconnecting = false;
  private readonly reconnectReasons = new Set<string>();
  private unexpectedDisconnect: boolean | null = null;
  private disconnectReason: string | null = null;
  private activateStartedMs: number | null = null;
  private microphoneEnabledMs: number | null = null;
  private activateCompletedMs: number | null = null;
  private leavePhase: KickstartLeavePhase | null = null;
  private reported = false;

  constructor(provider: MediaProvider, reporter: ClientMediaTelemetryReporter | undefined) {
    this.provider = provider;
    this.reporter = reporter;
  }

  start(sessionId: string | undefined): void {
    this.sessionId = sessionId ?? null;
    this.startedAtMs = performance.now();
    this.connectMs = null;
    this.joinMs = null;
    this.firstRemoteAudioMs = null;
    this.reconnectCount = 0;
    this.reconnecting = false;
    this.reconnectReasons.clear();
    this.unexpectedDisconnect = null;
    this.disconnectReason = null;
    this.activateStartedMs = null;
    this.microphoneEnabledMs = null;
    this.activateCompletedMs = null;
    this.leavePhase = null;
    this.reported = false;
  }

  markConnected(): void {
    this.connectMs = this.elapsedMs();
  }

  markJoined(): void {
    this.joinMs = this.elapsedMs();
  }

  markFirstRemoteAudio(): void {
    if (this.firstRemoteAudioMs === null) {
      this.firstRemoteAudioMs = this.elapsedMs();
    }
  }

  markActivateStarted(): void {
    this.activateStartedMs ??= this.debugElapsedMs();
  }

  markMicrophoneEnabled(): void {
    this.microphoneEnabledMs ??= this.debugElapsedMs();
  }

  markActivateCompleted(): void {
    this.activateCompletedMs ??= this.debugElapsedMs();
  }

  markLeavePhase(phase: KickstartLeavePhase | null): void {
    this.leavePhase = phase;
  }

  markReconnectStarted(reason: string): void {
    if (!this.reconnecting) {
      this.reconnectCount += 1;
    }
    this.reconnecting = true;
    this.reconnectReasons.add(reason);
  }

  markReconnected(): void {
    this.reconnecting = false;
  }

  markUnexpectedDisconnect(reason: string): void {
    this.unexpectedDisconnect = true;
    this.disconnectReason = reason;
  }

  markIntentionalDisconnect(): void {
    this.unexpectedDisconnect = false;
    this.disconnectReason = null;
  }

  async report(
    collectNetworkSnapshot: () => Promise<ClientTelemetryNetworkSnapshot>,
  ): Promise<void> {
    if (this.reported || this.sessionId === null || this.reporter === undefined) {
      return;
    }
    this.reported = true;
    let network = EMPTY_NETWORK_SNAPSHOT;
    try {
      network = await Promise.race([
        collectNetworkSnapshot(),
        new Promise<ClientTelemetryNetworkSnapshot>((resolve) => {
          window.setTimeout(() => resolve(EMPTY_NETWORK_SNAPSHOT), 750);
        }),
      ]);
    } catch {
      network = EMPTY_NETWORK_SNAPSHOT;
    }

    const payload: ClientMediaTelemetryPayload = {
      reportId: randomUuid(),
      provider: this.provider,
      final: true,
      sampleCount: network.sampleCount,
      transportProtocol: network.transportProtocol,
      localCandidateType: network.localCandidateType,
      remoteCandidateType: network.remoteCandidateType,
      relayUsed: network.relayUsed,
      rttMs: network.rttMs,
      jitterMs: network.jitterMs,
      packetLossPercent: network.packetLossPercent,
      bytesSent: network.bytesSent,
      bytesReceived: network.bytesReceived,
      reconnectCount: this.reconnectCount,
      reconnectReasons: [...this.reconnectReasons],
      connectMs: this.connectMs,
      joinMs: this.joinMs,
      firstRemoteAudioMs: this.firstRemoteAudioMs,
      unexpectedDisconnect: this.unexpectedDisconnect,
      disconnectReason: this.disconnectReason,
    };
    const debug: ClientMediaTelemetryDebug = {};
    if (this.activateStartedMs !== null) {
      debug.activateStartedMs = this.activateStartedMs;
    }
    if (this.microphoneEnabledMs !== null) {
      debug.microphoneEnabledMs = this.microphoneEnabledMs;
    }
    if (this.activateCompletedMs !== null) {
      debug.activateCompletedMs = this.activateCompletedMs;
    }
    if (this.leavePhase !== null) {
      debug.leavePhase = this.leavePhase;
    }
    if (Object.keys(debug).length > 0) {
      payload.debug = debug;
    }
    void this.reporter(this.sessionId, payload).catch(() => undefined);
  }

  private elapsedMs(): number | null {
    if (this.startedAtMs === null) {
      return null;
    }
    return Math.round(Math.max(0, performance.now() - this.startedAtMs));
  }

  private debugElapsedMs(): number | null {
    const elapsed = this.elapsedMs();
    return elapsed === null ? null : Math.min(elapsed, MAX_DEBUG_ELAPSED_MS);
  }
}

export function parseRtcStatsReports(
  reports: RTCStatsReport[],
): ClientTelemetryNetworkSnapshot {
  const statsById = new Map<string, Record<string, unknown>>();
  for (const report of reports) {
    report.forEach((value: unknown) => {
      if (typeof value !== 'object' || value === null) {
        return;
      }
      const stat = value as Record<string, unknown>;
      const id = typeof stat.id === 'string'
        ? stat.id
        : `${String(stat.type)}-${statsById.size}`;
      statsById.set(id, stat);
    });
  }
  if (statsById.size === 0) {
    return EMPTY_NETWORK_SNAPSHOT;
  }

  const inboundAudio = [...statsById.values()].filter((stat) => (
    stat.type === 'inbound-rtp'
    && (stat.kind === undefined || stat.kind === 'audio' || stat.mediaType === 'audio')
  ));
  let bytesSent = 0;
  let hasBytesSent = false;
  let bytesReceived = 0;
  let hasBytesReceived = false;
  let packetsLost = 0;
  let packetsReceived = 0;
  let hasPacketCounts = false;
  const jittersMs: number[] = [];
  const fallbackRttMs: number[] = [];

  for (const stat of statsById.values()) {
    if (
      stat.type === 'outbound-rtp'
      && (stat.kind === undefined || stat.kind === 'audio' || stat.mediaType === 'audio')
    ) {
      const value = finiteNonnegativeNumber(stat.bytesSent);
      if (value !== null) {
        bytesSent += value;
        hasBytesSent = true;
      }
    }
    if (stat.type === 'remote-inbound-rtp') {
      const value = finiteNonnegativeNumber(stat.roundTripTime);
      if (value !== null) {
        fallbackRttMs.push(value * 1000);
      }
    }
  }
  for (const stat of inboundAudio) {
    const receivedBytes = finiteNonnegativeNumber(stat.bytesReceived);
    if (receivedBytes !== null) {
      bytesReceived += receivedBytes;
      hasBytesReceived = true;
    }
    const receivedPackets = finiteNonnegativeNumber(stat.packetsReceived);
    const lostPackets = finiteNonnegativeNumber(stat.packetsLost);
    if (receivedPackets !== null && lostPackets !== null) {
      packetsReceived += receivedPackets;
      packetsLost += lostPackets;
      hasPacketCounts = true;
    }
    const jitter = finiteNonnegativeNumber(stat.jitter);
    if (jitter !== null) {
      jittersMs.push(jitter * 1000);
    }
  }

  const transport = [...statsById.values()].find(
    (stat) => stat.type === 'transport' && typeof stat.selectedCandidatePairId === 'string',
  );
  const selectedPairId = typeof transport?.selectedCandidatePairId === 'string'
    ? transport.selectedCandidatePairId
    : null;
  const candidatePair = selectedPairId === null
    ? [...statsById.values()].find((stat) => (
      stat.type === 'candidate-pair'
      && stat.nominated === true
      && stat.state === 'succeeded'
    ))
    : statsById.get(selectedPairId);
  const localCandidate = typeof candidatePair?.localCandidateId === 'string'
    ? statsById.get(candidatePair.localCandidateId)
    : undefined;
  const remoteCandidate = typeof candidatePair?.remoteCandidateId === 'string'
    ? statsById.get(candidatePair.remoteCandidateId)
    : undefined;
  const localCandidateType = candidateType(localCandidate?.candidateType);
  const remoteCandidateType = candidateType(remoteCandidate?.candidateType);
  const transportProtocol = protocol(
    localCandidate?.relayProtocol ?? localCandidate?.protocol,
  );
  const candidateRttSeconds = finiteNonnegativeNumber(candidatePair?.currentRoundTripTime);
  const packetDenominator = packetsReceived + packetsLost;

  return {
    sampleCount: 1,
    transportProtocol,
    localCandidateType,
    remoteCandidateType,
    relayUsed: localCandidateType === 'relay' || remoteCandidateType === 'relay'
      ? true
      : localCandidateType !== null && remoteCandidateType !== null
        ? false
        : null,
    rttMs: candidateRttSeconds === null
      ? maximumOrNull(fallbackRttMs)
      : candidateRttSeconds * 1000,
    jitterMs: maximumOrNull(jittersMs),
    packetLossPercent: hasPacketCounts && packetDenominator > 0
      ? packetsLost / packetDenominator * 100
      : null,
    bytesSent: hasBytesSent ? bytesSent : null,
    bytesReceived: hasBytesReceived ? bytesReceived : null,
  };
}

export function parseDailyNetworkStats(value: unknown): ClientTelemetryNetworkSnapshot {
  if (typeof value !== 'object' || value === null) {
    return EMPTY_NETWORK_SNAPSHOT;
  }
  const stats = (value as Record<string, unknown>).stats;
  if (typeof stats !== 'object' || stats === null) {
    return EMPTY_NETWORK_SNAPSHOT;
  }
  const latest = (stats as Record<string, unknown>).latest;
  if (typeof latest !== 'object' || latest === null) {
    return EMPTY_NETWORK_SNAPSHOT;
  }
  const latestStats = latest as Record<string, unknown>;
  const rttSeconds = finiteNonnegativeNumber(latestStats.networkRoundTripTime);
  const jitterSeconds = finiteNonnegativeNumber(latestStats.audioRecvJitter);
  const packetLossRatio = finiteNonnegativeNumber(latestStats.audioRecvPacketLoss);

  return {
    ...EMPTY_NETWORK_SNAPSHOT,
    sampleCount: 1,
    rttMs: rttSeconds === null ? null : rttSeconds * 1000,
    jitterMs: jitterSeconds === null ? null : jitterSeconds * 1000,
    packetLossPercent: packetLossRatio === null ? null : packetLossRatio * 100,
  };
}

export type JitterBufferTotals = {
  delaySeconds: number;
  emittedCount: number;
};

// Cumulative receive jitter buffer totals of the inbound audio (tutor audio) streams.
export function parseJitterBufferTotals(reports: RTCStatsReport[]): JitterBufferTotals | null {
  let delaySeconds = 0;
  let emittedCount = 0;
  let found = false;
  for (const report of reports) {
    report.forEach((value: unknown) => {
      if (typeof value !== 'object' || value === null) {
        return;
      }
      const stat = value as Record<string, unknown>;
      if (
        stat.type !== 'inbound-rtp'
        || !(stat.kind === undefined || stat.kind === 'audio' || stat.mediaType === 'audio')
      ) {
        return;
      }
      const delay = finiteNonnegativeNumber(stat.jitterBufferDelay);
      const emitted = finiteNonnegativeNumber(stat.jitterBufferEmittedCount);
      if (delay !== null && emitted !== null) {
        delaySeconds += delay;
        emittedCount += emitted;
        found = true;
      }
    });
  }
  return found ? { delaySeconds, emittedCount } : null;
}

// Average jitter buffer delay of the audio emitted since the previous totals, in ms; null when
// nothing played in between. Totals that went backwards (receiver replaced) count from zero.
export function jitterBufferWindowMs(
  previous: JitterBufferTotals | null,
  current: JitterBufferTotals | null,
): number | null {
  if (current === null) {
    return null;
  }
  const base = previous !== null
    && current.emittedCount >= previous.emittedCount
    && current.delaySeconds >= previous.delaySeconds
    ? previous
    : { delaySeconds: 0, emittedCount: 0 };
  const emitted = current.emittedCount - base.emittedCount;
  return emitted > 0 ? (current.delaySeconds - base.delaySeconds) / emitted * 1000 : null;
}

function finiteNonnegativeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function maximumOrNull(values: number[]): number | null {
  return values.length === 0 ? null : Math.max(...values);
}

function candidateType(value: unknown): CandidateType | null {
  return value === 'host' || value === 'srflx' || value === 'prflx' || value === 'relay'
    ? value
    : null;
}

function protocol(value: unknown): TransportProtocol | null {
  return value === 'udp' || value === 'tcp' || value === 'tls' ? value : null;
}
